import { useState, useEffect, useCallback } from 'react';

import palette from '@lib/core/src/theme/palette';
import CheckIcon from '@mui/icons-material/Check';
import { Avatar } from '@mui/material';

import { AccountUserDto, AccountUserDtoType } from '@/api/generated';

import InputAndAvatars from './InputAndAvatars';
import { Container, Row, Text } from './UserSelectStyles';
import UserSelectVariant from './UserSelectVariant';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

interface UserSelectProps {
  users: AccountUserDto[];
  selectedUsers: AccountUserDto[];
  setSelectedUsers: (users: AccountUserDto[]) => void;
  placeholder?: string;
  variant?: UserSelectVariant;
  disabled?: boolean;
}

const UserSelect = ({
  users,
  selectedUsers,
  setSelectedUsers,
  placeholder = 'Select or type email...',
  variant = UserSelectVariant.email,
  disabled = false
}: UserSelectProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [isInputInList, setIsInputInList] = useState<boolean>(false);
  const [isInputValid, setIsInputValid] = useState<boolean>(true);
  const [localUsers, setLocalUsers] = useState<AccountUserDto[]>([]);

  const setOpenEnabled = useCallback(() => {
    if (disabled) return;
    setOpen(prev => !prev);
  }, [disabled]);

  useEffect(() => {
    setLocalUsers(users.filter(user => user.type !== AccountUserDtoType.assigneeUser));
  }, [users]);

  const onUsersSelected = useCallback(
    (event: React.SyntheticEvent, value: AccountUserDto[] | string[] | unknown) => {
      setOpen(true);
      const newSelectedList: AccountUserDto[] = [];
      const newLocalUsersList: AccountUserDto[] = [];

      if (!Array.isArray(value)) {
        return;
      }

      (value as AccountUserDto[]).forEach(user => {
        if (typeof user === 'string') {
          if (isInputValid && !isInputInList) {
            let email = '';
            let firstName = '';
            let lastName = '';

            if (variant === UserSelectVariant.email) {
              email = user;
              firstName = user;
            } else {
              const userData = (user as string).split(' ');
              firstName = userData[0];
              lastName = userData[1] || '';
            }

            const newUser = { id: user, email, firstName, lastName };

            newLocalUsersList.push(newUser);
            newSelectedList.push(newUser);
          }
        } else {
          newSelectedList.push(user);
        }
      });

      setLocalUsers(prev => [...newLocalUsersList, ...prev]);
      setSelectedUsers(newSelectedList);
    },
    [isInputValid, isInputInList, variant, setSelectedUsers, setLocalUsers]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, newInputValue: string) => {
      setInputValue(newInputValue);
      if (variant === UserSelectVariant.email) {
        if (newInputValue.length === 0) {
          setIsInputValid(true);
          return;
        }
        setIsInputValid(EMAIL_REGEX.test(newInputValue));
      }
      if (users.some(user => user.email?.includes(newInputValue))) {
        setIsInputInList(true);
      } else {
        setIsInputInList(false);
      }
    },
    [variant, setInputValue, setIsInputValid, setIsInputInList, users]
  );

  const sortBySelectedUsers = (users: AccountUserDto[]) =>
    users.sort(
      (a, b) =>
        selectedUsers.findIndex(user => user.id === b.id) -
        selectedUsers.findIndex(user => user.id === a.id)
    );

  const smallAvatars = selectedUsers.slice(0, 4);

  const smallAvatarsWidth = Math.min(selectedUsers.length * 15, 15 * 5);

  return (
    <Container
      multiple
      freeSolo
      disablePortal
      open={open}
      disabled={disabled}
      disableClearable
      getOptionLabel={option => {
        const user = option as AccountUserDto;
        return user ? user.firstName + ' ' + user.lastName : '';
      }}
      onOpen={() => setOpenEnabled()}
      onClose={(event, reason) => {
        if (reason === 'blur' || reason === 'escape') {
          setOpen(false); // Close only if clicked outside or Esc pressed
          setLocalUsers(prev => sortBySelectedUsers(prev));
        }
      }}
      options={localUsers}
      value={selectedUsers}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={onUsersSelected}
      renderTags={() => null}
      filterOptions={(options, { inputValue }) =>
        (options as AccountUserDto[]).filter((option: AccountUserDto) =>
          (
            (option?.email || '').toLowerCase() +
            (option?.firstName || '').toLowerCase() +
            (option?.lastName || '').toLowerCase()
          ).includes(inputValue.toLowerCase())
        )
      }
      renderInput={params => (
        <InputAndAvatars
          smallAvatarsWidth={smallAvatarsWidth}
          smallAvatars={smallAvatars}
          setOpen={setOpenEnabled}
          placeholder={placeholder}
          params={params}
          isInputValid={isInputValid}
          isInputInList={isInputInList}
          selectedUsers={selectedUsers}
        />
      )}
      renderOption={(props, option, { selected }) => {
        const user = option as AccountUserDto;
        const { key, ...otherProps } = props;
        return (
          <Row key={user.id || user.email || key} {...otherProps}>
            <Avatar
              sx={{
                fontSize: '12px',
                width: '30px',
                height: '30px',
                backgroundColor: palette.white,
                color: palette.grey[500]
              }}
              src={user.photoUrl}
            >
              {user.photoUrl
                ? ''
                : user.firstName?.slice(0, 2)?.toUpperCase() ||
                  user.email?.slice(0, 2)?.toUpperCase()}
            </Avatar>
            <Text>{variant === 'email' ? user.email : user.firstName + ' ' + user.lastName}</Text>
            {selected && (
              <CheckIcon
                sx={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: palette.blue[100],
                  fontSize: '10px'
                }}
              />
            )}
          </Row>
        );
      }}
    />
  );
};

export default UserSelect;
